import CloseIcon from '@mui/icons-material/Close'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DownloadIcon from '@mui/icons-material/Download'
import FacebookOutlinedIcon from '@mui/icons-material/FacebookOutlined'
import ShareIcon from '@mui/icons-material/Share'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import XIcon from '@mui/icons-material/X'
import { clsx } from 'clsx'
import type React from 'react'
import { FacebookShareButton, WhatsappShareButton, XShareButton } from 'react-share'

/** True on touch-primary devices (phones/tablets); false on laptop/desktop. */
function isMobileDevice(): boolean {
  return window.matchMedia('(pointer: coarse)').matches
}

const SITE_URL = 'https://emotionswheel.app'
const SHARE_TEXT = 'Find your affirmation ✨'

type OverlayProps = {
  onClick: () => void
  onShare: () => Promise<void>
  onDownload: () => Promise<void>
  onCopy: () => void
  isClosing: boolean
}

export default function Overlay({ onClick, onShare, onDownload, onCopy, isClosing }: OverlayProps) {
  const mobileShare = isMobileDevice()

  function stopAndRun(fn: () => void) {
    return (e: React.MouseEvent) => {
      e.stopPropagation()
      fn()
    }
  }

  return (
    <div
      className={clsx('choice-overlay z-10', isClosing && 'choice-overlay--closing')}
      onClick={onClick}
    >
      <div className="choice-overlay-header w-full gap-2 md:gap-3">
        <span className="choice-overlay-header__title text-2xl font-medium tracking-wide lg:text-3xl">
          You chose this card for a reason
        </span>
        <span className="choice-overlay-header__subtitle text-xl font-normal italic lg:text-2xl">
          Take a breath. Let the words settle in.
        </span>
      </div>

      <div className="choice-overlay-actions">
        <div className="choice-overlay-icon-group" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className="choice-overlay-icon-btn"
            aria-label="Ask again"
            title="Ask again"
            onClick={onClick}
          >
            <CloseIcon />
          </button>
          <span />

          {/* Download */}
          {!mobileShare && (
            <button
              type="button"
              className="choice-overlay-icon-btn"
              aria-label="Save image"
              title="Save image"
              onClick={stopAndRun(() => void onDownload())}
            >
              <DownloadIcon />
            </button>
          )}

          <button
            type="button"
            className="choice-overlay-icon-btn"
            aria-label="Copy text"
            title="Copy text"
            onClick={stopAndRun(() => void onCopy())}
          >
            <ContentCopyIcon />
          </button>

          {/* Mobile: native share */}
          {mobileShare && (
            <button
              type="button"
              className="choice-overlay-icon-btn"
              aria-label="Share"
              title="Share"
              onClick={stopAndRun(() => void onShare())}
            >
              <ShareIcon />
            </button>
          )}

          {/* Desktop: social share links */}
          {!mobileShare && (
            <>
              <FacebookShareButton
                url={SITE_URL}
                hashtag="affirmations"
                aria-label="Share this page on Facebook"
                htmlTitle="Share this page on Facebook"
              >
                <span className="choice-overlay-icon-btn choice-overlay-icon-btn--fb">
                  <FacebookOutlinedIcon />
                </span>
              </FacebookShareButton>
              <XShareButton
                title={SHARE_TEXT}
                hashtags={['affirmations']}
                url={SITE_URL}
                aria-label="Share on X (formerly Twitter)"
                htmlTitle="Share on X (formerly Twitter)"
              >
                <span className="choice-overlay-icon-btn choice-overlay-icon-btn--x">
                  <XIcon />
                </span>
              </XShareButton>
              <WhatsappShareButton
                title={SHARE_TEXT}
                url={SITE_URL}
                aria-label="Share on WhatsApp"
                htmlTitle="Share on WhatsApp"
              >
                <span className="choice-overlay-icon-btn choice-overlay-icon-btn--wa">
                  <WhatsAppIcon />
                </span>
              </WhatsappShareButton>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
